import {
    Alert,
    alpha,
    Box,
    Button,
    FormLabel,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { FormComponentValue } from 'components/common/form/layout/control';
import IconMi from 'components/common/icon/IconMi';
import { FormDataAPIType } from 'components/common/form';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { Popup } from 'components/common/popup/Popup';
import ReactHookFormController from 'components/common/form/layout/ReactHookFormController';
import useCustomSimplifiedForm from 'components/common/form/hooks/useCustomSimplifiedForm';
import { useTranslation } from 'react-i18next';
import { AlertRuleScreenProps } from 'components/alert-rule-popup/index';
import { alertRuleAPI } from 'api/alert-wizard';
import AlertRulePreview from 'components/alert-rule-popup/parts/AlertRulePreview';
import AlertRuleSendTestConfirmPopup from 'components/alert-rule-popup/parts/AlertRuleSendTestConfirmPopup';

interface AlertRuleScreenContentProps extends AlertRuleScreenProps {
    onChange?: (text: any) => void;
}

export default function AlertRuleScreenContent(props: AlertRuleScreenContentProps) {
    const { alertRuleData, wizardData, updateAlertRuleData = () => {}, updateWizardData = () => {} } = props;
    const { t } = useTranslation(['components/alert-rule-popup/alert_rule_popup']);

    const [isHistoryVisualizationElement, setIsHistoryVisualizationElement] = useState(false);
    const [patternPopupKey, setPatternPopupKey] = useState('');
    const [patterns, setPatterns] = useState<string[]>([]);
    const [sendTestConfirmPopupData, setSendTestConfirmPopupData] = useState<any>(null);

    const patternFieldName = 'pattern';

    useEffect(() => {
        const currentElement = wizardData.form.visualization_element_id.data.find((item: any) => {
            return item.value == alertRuleData.visualization_element_id;
        });
        if (currentElement) {
            setIsHistoryVisualizationElement(currentElement.history);
        }

        //initSendAlertToPhone
        if ('sms' == alertRuleData.send_notification_via && 'Y' == alertRuleData.send_alert_to_phone) {
            updateAlertRuleData({ send_notification_via: 'phone_alert' });
        }
    }, []);

    useEffect(() => {
        if (patternPopupKey && !patterns.length) {
            //getPatterns
            alertRuleAPI
                .getAlertRulePatterns(wizardData.dashboardElement.element_id)
                .then((response) => {
                    setPatterns(Object.values(response.data.patterns));
                })
                .catch(() => {});
        }
    }, [patternPopupKey]);

    useEffect(() => {
        //initSendAlertToPhone
        updateAlertRuleData({ send_alert_to_phone: alertRuleData.send_notification_via == 'phone_alert' ? 'Y' : 'N' });
    }, [alertRuleData.send_notification_via]);

    const alert_payload_select: FormComponentValue[] = Object.keys(wizardData.form.visualization_type.data).map(
        (key: string) => {
            return {
                label: wizardData.form.visualization_type.data[key],
                value: key,
            };
        },
    );

    const visualization_element_id_select: FormComponentValue[] = wizardData.form.visualization_element_id.data.map(
        (item: any) => {
            return {
                label: item.label,
                value: item.value,
                props: {
                    history: item.history,
                    segment_value_id: item.segment_value_id,
                },
            };
        },
    );

    useEffect(() => {
        elementProps.form.hookFormSetValue(patternFieldName, elementProps.form.hookFormGetValues(patternPopupKey));
    }, [patternPopupKey]);

    const elementProps: FormDataAPIType = useCustomSimplifiedForm({
        subject_format_email: alertRuleData.subject_format_email,
        subject_format_sms: alertRuleData.subject_format_sms,
        display_format: alertRuleData.display_format,
        additional_message: alertRuleData.additional_message,
        match_measurement_time_ind: alertRuleData.match_measurement_time_ind,
    });

    const matchMeasurementTime = elementProps.form.hookFormWatch('match_measurement_time_ind');

    useEffect(() => {
        updateAlertRuleData({ match_measurement_time_ind: matchMeasurementTime });
    }, [matchMeasurementTime]);

    const getIconLabel = (key: string, label: string) => {
        let labelIcon = '';
        /*switch (key) {
            case 'subject_format_email':
                labelIcon = 'mail';
                break;
            case 'subject_format_sms':
                labelIcon = 'mobile';
                break;
            case 'alert_payload':
                labelIcon = 'link';
                break;
        }*/

        if (!labelIcon) return <FormLabel>{label}</FormLabel>;

        return (
            <Stack direction={'row'} alignItems={'center'}>
                <Box sx={{ pb: 0.5, pr: 0.5, display: 'inline-flex' }}>
                    <IconMi icon={labelIcon} fontSize={'16'} />
                </Box>
                <FormLabel sx={{ lineHeight: '15px' }}>{label}</FormLabel>
            </Stack>
        );
    };

    const getPatternTargetField = (key: string) => {
        return (
            <Box
                onClick={() => {
                    setPatternPopupKey(key);
                }}
            >
                {getIconLabel(key, t(`form.content.pattern_field.${key}.label`))}
                <ReactHookFormController
                    elementProps={prepareFormElementProps({
                        ...elementProps,
                        component: {
                            component: 'FormText',
                            name: key,
                            label: '',
                            props: { readOnly: true },
                        },
                    })}
                />
            </Box>
        );
    };

    const sendTestAction = () => {
        alertRuleAPI
            .checkDeliveryTypes()
            .then((response) => {
                if (response.data.status == 'OK') {
                    const responseData = response.data.data;
                    updateWizardData({
                        //isChangedDeliveryTypes
                        isPhoneAlert: responseData.allowPhoneAlert,
                        isSmsAlert: responseData.allowSmsAlert,
                    });

                    alertRuleAPI
                        .confirmSendTestData(
                            alertRuleData.preview_for,
                            alertRuleData.alert_rule_id,
                            alertRuleData.element_id,
                        )
                        .then((response) => {
                            setSendTestConfirmPopupData(response.data);
                        })
                        .catch(() => {});
                }
            })
            .catch(() => {});
    };

    const getTooltipTestBtn = () => {
        return t('form.preview.send_test_btn_tooltip.' + alertRuleData.preview_for, {
            email: wizardData.userSendData.email,
        });
    };

    return (
        <>
            <Stack spacing={3} direction={'row'}>
                <Box sx={{ width: 1, overflow: 'hidden' }}>
                    <Typography
                        variant="heading"
                        sx={{ borderBottomColor: (theme) => alpha(theme.palette.primary.main, 0.4), mb: 2 }}
                    >
                        {t('form.content.section_title')}
                    </Typography>
                    <Box>{getPatternTargetField('subject_format_email')}</Box>
                    <Box sx={{ mt: 2 }}>{getPatternTargetField('subject_format_sms')}</Box>
                    <Box sx={{ mt: 2 }}>{getPatternTargetField('display_format')}</Box>
                    <Box sx={{ mt: 2 }}>
                        {getIconLabel('alert_payload', t('form.content.label_alert_payload'))}
                        <ReactSelect
                            dataTest={`alert-rule-screen-payload`}
                            data={alert_payload_select}
                            value={[alertRuleData.visualization_type]}
                            update={(val) => {
                                updateAlertRuleData({ visualization_type: val });
                            }}
                        />
                    </Box>
                    {alertRuleData.visualization_type == 'other' && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <ReactSelect
                                    dataTest={`alert-rule-screen-other`}
                                    data={visualization_element_id_select}
                                    value={[alertRuleData.visualization_element_id]}
                                    update={(val, fullValue) => {
                                        updateAlertRuleData({
                                            visualization_element_id: val,
                                            visualization_segment_value_id: fullValue.props.segment_value_id,
                                        });
                                        setIsHistoryVisualizationElement(fullValue.props.history == 'Y');
                                    }}
                                />
                            </Box>
                            {isHistoryVisualizationElement && (
                                <Box sx={{ mt: 1 }}>
                                    <ReactHookFormController
                                        elementProps={prepareFormElementProps({
                                            ...elementProps,
                                            component: {
                                                component: 'FormCheckbox',
                                                name: 'match_measurement_time_ind',
                                                label: t('form.content.match_measurement_time_payload'),
                                                props: {},
                                            },
                                        })}
                                    />
                                </Box>
                            )}
                        </>
                    )}
                    <Box sx={{ mt: 2 }}>
                        <Box
                            onClick={() => {
                                setPatternPopupKey('additional_message');
                            }}
                            sx={{
                                '.additional-message-field textarea': {
                                    height:
                                        alertRuleData.visualization_type != 'other'
                                            ? '235px !important'
                                            : isHistoryVisualizationElement
                                              ? '171px !important'
                                              : undefined,
                                },
                            }}
                        >
                            <FormLabel>{t(`form.content.pattern_field.additional_message.label`)}</FormLabel>
                            <ReactHookFormController
                                elementProps={prepareFormElementProps({
                                    ...elementProps,
                                    component: {
                                        component: 'FormTextarea',
                                        name: 'additional_message',
                                        props: {
                                            controlClass: 'additional-message-field',
                                            readOnly: true,
                                            rows: 11,
                                            placeholder: t(`form.content.pattern_field.additional_message.placeholder`),
                                        },
                                    },
                                })}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ width: 1, overflow: 'hidden' }}>
                    <Typography
                        variant="heading"
                        sx={{ borderBottomColor: (theme) => alpha(theme.palette.primary.main, 0.4), mb: 2 }}
                    >
                        {t('form.preview.section_title')}
                    </Typography>
                    <FormLabel>{t('form.preview.send_test_for_label')}</FormLabel>
                    <Stack direction={'row'} spacing={1} alignItems={'start'}>
                        <ToggleButtonGroup
                            color="primary"
                            value={alertRuleData.preview_for}
                            exclusive
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    updateAlertRuleData({ preview_for: newValue });
                                }
                            }}
                            sx={{ minWidth: '252px' }}
                        >
                            {['email', 'push', 'sms'].map((item) => {
                                return (
                                    <ToggleButton key={item} value={item}>
                                        {t('form.preview.preview_for_option.' + item)}
                                    </ToggleButton>
                                );
                            })}
                        </ToggleButtonGroup>

                        <Tooltip title={getTooltipTestBtn()}>
                            <Button
                                variant="light"
                                startIcon={
                                    <IconMi
                                        icon={
                                            alertRuleData.preview_for == 'email'
                                                ? 'email-template'
                                                : alertRuleData.preview_for == 'sms'
                                                  ? 'comment-2'
                                                  : 'mobile'
                                        }
                                    />
                                }
                                onClick={sendTestAction}
                            >
                                {t('form.preview.send_test_btn')}
                            </Button>
                        </Tooltip>
                    </Stack>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                            borderRadius: 1,
                            mt: 2,
                            height: 447,
                            overflow: 'auto',
                            position: 'relative',
                        }}
                    >
                        <AlertRulePreview {...props} />
                    </Box>
                </Box>
            </Stack>

            <Popup
                settings={{
                    title: t(`form.content.pattern_field.${patternPopupKey}.popup_title`),
                }}
                open={!!patternPopupKey && patterns.length > 0}
                key={patternPopupKey}
                onHide={() => {
                    setPatternPopupKey('');
                    elementProps.form.hookFormSetValue(patternFieldName, '');
                }}
                onConfirm={() => {
                    setPatternPopupKey('');
                    elementProps.form.hookFormSetValue(
                        patternPopupKey,
                        elementProps.form.hookFormGetValues(patternFieldName),
                    );
                    updateAlertRuleData({ [patternPopupKey]: elementProps.form.hookFormGetValues(patternFieldName) });
                    elementProps.form.hookFormSetValue(patternFieldName, '');
                }}
                maxWidth="popupMd"
            >
                {patternPopupKey === 'additional_message' && (
                    <FormLabel>{t(`form.content.pattern_field.${patternPopupKey}.popup_label`)}</FormLabel>
                )}

                <ReactHookFormController
                    elementProps={prepareFormElementProps({
                        ...elementProps,
                        component: {
                            component: 'FormTemplateField',
                            name: patternFieldName,
                            label: t(`form.content.pattern_field.${patternPopupKey}.popup_label`),
                            props: {
                                rows: 11,
                                placeholder:
                                    patternPopupKey === 'additional_message'
                                        ? t(`form.content.pattern_field.additional_message.placeholder`)
                                        : undefined,
                                patterns: patterns,
                                multiline: patternPopupKey === 'additional_message' ? true : undefined,
                                maxLength: patternPopupKey === 'additional_message' ? 1000 : undefined,
                                charactersLeftLabel: 'field_characters_left',
                                onChange: (val: any) => {
                                    elementProps.form.hookFormSetValue(patternFieldName, val);
                                },
                            },
                        },
                    })}
                />

                {patternPopupKey === 'additional_message' && (
                    <Alert severity={'info'} variant={'standard'} sx={{ mt: 2 }}>
                        {t(`form.content.pattern_field.additional_message.limit_characters_msg`)}
                    </Alert>
                )}
            </Popup>
            {sendTestConfirmPopupData && (
                <AlertRuleSendTestConfirmPopup
                    {...props}
                    confirmData={sendTestConfirmPopupData}
                    onClose={() => {
                        setSendTestConfirmPopupData(null);
                    }}
                />
            )}
        </>
    );
}
